import React from "react";
import ReactDOM from "react-dom/client";

import "./index.css";

import { createAppHistory } from "./appHistory";
import { isElectron } from "./env";
import { resolveCloudClerkMount } from "./cloud/publicConfig";
import { getRouter } from "./router";
import { bindWorldUrlRouter } from "./neucleos/worldStore";
import {
  syncDocumentElectronPlatformClasses,
  syncDocumentWindowControlsOverlayClass,
} from "./lib/windowControlsOverlay";
import { AppRoot } from "./AppRoot";
import { clearChunkReloadGuard, reloadOnceForChunkLoadError } from "./mainChunkReloadGuard";

// Electron loads the app from a file-backed shell, so hash history avoids path
// resolution issues; the decision (and the URL grammar that follows it) lives
// in the appHistory seam.
const history = createAppHistory(isElectron);

const router = getRouter(history);
// The World URL repair goes through the router, never behind it (W-c83c3467).
bindWorldUrlRouter(router as never);

if (isElectron) {
  syncDocumentElectronPlatformClasses(navigator.platform);
  syncDocumentWindowControlsOverlayClass();
}

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

// §7.6: a production key locked to another apex can never serve this origin —
// booting Clerk anyway floods the console with refused requests. Say why
// once. resolveCloudClerkMount is THE mount law; every Clerk-hook consumer
// gates on the same answer, so skipping here never strands a useAuth.
const clerkMount = resolveCloudClerkMount();
if (clerkMount.reason !== null) {
  console.info(clerkMount.reason);
}

// A failed split-chunk fetch usually means the hashed assets went stale under
// a deploy or a desktop runtime swap; one guarded reload picks up the fresh
// index.html.
let chunkLoadFailed = false;
let reloadScheduled = false;
window.addEventListener("vite:preloadError", (event) => {
  chunkLoadFailed = true;
  if (reloadOnceForChunkLoadError()) {
    reloadScheduled = true;
    event.preventDefault();
  }
});

const app = <AppRoot router={router} />;

// Managed auth is cloud-only, and the Electron Clerk provider bundles the full
// clerk-js runtime. Loading only the selected runtime as a split chunk keeps
// every Clerk byte out of the startup graph in local mode, and keeps the
// bundled clerk-js out of the browser build entirely (UP5 cold-start split,
// after pingdotgg/t3code #9058, MIT).
const managedAuthShellModule =
  clerkPublishableKey && clerkMount.mount
    ? isElectron
      ? import("./mainManagedAuthShellElectron")
      : import("./mainManagedAuthShellBrowser")
    : null;

// The index.html boot splash lives inside #root, and React's first commit
// clears it. Resolve everything that first commit needs, the selected
// managed-auth runtime and the initial route's split chunks, before
// rendering, so the splash holds until real UI paints instead of dropping
// to a blank window while chunks download.
void Promise.all([
  managedAuthShellModule?.then((module) => module.default) ?? null,
  router.load(),
  isElectron ? import("./observability/sentry").catch(() => null) : null,
])
  .then(([ManagedAuthShell, , diagnostics]) => {
    // A route chunk failure still resolves router.load(): the error is parked
    // in the lazy component and surfaces through the route error boundary.
    // Skip the paint when a reload is on its way, and only re-arm the guard
    // after a boot that fetched every chunk it asked for.
    if (reloadScheduled) return;
    if (!chunkLoadFailed) clearChunkReloadGuard();
    ReactDOM.createRoot(
      document.getElementById("root") as HTMLElement,
      diagnostics?.rootErrorOptions,
    ).render(
      <React.StrictMode>
        {ManagedAuthShell && clerkPublishableKey ? (
          <ManagedAuthShell publishableKey={clerkPublishableKey}>{app}</ManagedAuthShell>
        ) : (
          app
        )}
      </React.StrictMode>,
    );
  })
  .catch((error: unknown) => {
    // The auth shell chunk failed and the guarded reload is spent. Say so
    // instead of leaving the splash up forever.
    if (reloadScheduled) return;
    console.error("Cleo failed to load its startup chunks.", error);
    const bootShell = document.getElementById("boot-shell");
    if (bootShell) bootShell.textContent = "Cleo could not load. Reload to try again.";
  });
